// Stage News, Sports (stories + live scores) and Sound pages on the web, same feeds as the app.
import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ExternalLink, Music2, Newspaper, Pause, Play, Radio, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageMeta } from "@/lib/site";
import { api } from "@/lib/api";
import { AgentBrief } from "@/components/agents/Agents";
import { Flag } from "@/lib/flags";

const cx = (...a) => a.filter(Boolean).join(" ");
const ago = (s) => { if (!s) return ""; const m = Math.max(1, Math.round((Date.now() - new Date(s).getTime()) / 60000)); return m < 60 ? m + "m" : m < 1440 ? Math.round(m / 60) + "h" : Math.round(m / 1440) + "d"; };

function StoryCard({ s }) {
  const img = (s.media && s.media.image) || s.image_url;
  const big = s.size === "big";
  return (
    <article className={cx("overflow-hidden rounded-2xl border border-border bg-card", big && "sm:col-span-2")} data-testid={"story-" + s.id}>
      {img && <img src={img} alt="" loading="lazy" className={cx("w-full object-cover", big ? "aspect-[16/8] max-h-[420px]" : "aspect-[16/9]")} />}
      <div className="p-4">
        <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="font-semibold text-[#E0007A]">{s.league_label || s.category_label}</span>
          {s.status === "unconfirmed" && <span className="rounded-full bg-amber-500/10 px-2 py-0.5 font-semibold text-amber-700">Developing</span>}
          <span>{ago(s.published_at)} ago</span>
        </p>
        <h2 className={cx("mt-1 font-bold leading-snug", big ? "text-xl" : "text-base")}>{s.headline}</h2>
        {(s.summary || s.excerpt) && <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{s.summary || s.excerpt}</p>}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {(s.sources || []).slice(0, 4).map((src) => <a key={src.outlet_id + src.url} href={src.url} target="_blank" rel="noopener" className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs font-medium hover:bg-muted/70">{src.name} <ExternalLink size={11} /></a>)}
          {s.source_count > 4 && <span className="text-xs text-muted-foreground">+{s.source_count - 4} more</span>}
          {(s.actions || []).slice(0, 2).map((a) => a.url ? <a key={a.url} href={a.url} target="_blank" rel="noopener sponsored" className="rounded-full border border-[#E0007A]/40 px-2.5 py-1 text-xs font-semibold text-[#E0007A]">{a.label}</a> : null)}
        </div>
      </div>
    </article>
  );
}

function Chips({ items, value, onPick }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]" role="tablist">
      {items.map((c) => <button key={c.key} type="button" role="tab" aria-selected={value === c.key} onClick={() => onPick(c.key)} className={cx("whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm font-semibold", value === c.key ? "border-transparent bg-[#E0007A] text-white" : "border-border bg-card hover:bg-muted")}>{c.label}{c.count ? " · " + c.count : ""}</button>)}
    </div>
  );
}

function useStories(path, deps) {
  const [page, setPage] = useState(0);
  const [data, setData] = useState(undefined);
  const [items, setItems] = useState([]);
  useEffect(() => { setPage(0); setItems([]); setData(undefined); }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let alive = true;
    api(path(page)).then((d) => { if (!alive) return; setData(d); setItems((prev) => (page === 0 ? d.items : [...prev, ...d.items.filter((x) => !prev.some((p) => p.id === x.id))])); }).catch(() => alive && setData(null));
    return () => { alive = false; };
  }, [page, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
  return { data, items, more: () => setPage((p) => p + 1) };
}

export function StageNews() {
  usePageMeta("News", "Today's stories, checked across outlets, on Fable + Stage.");
  const [p, setP] = useSearchParams();
  const cat = p.get("category") || "top";
  const scope = p.get("scope") || "all";
  const { data, items, more } = useStories((pg) => "/news?category=" + cat + "&scope=" + scope + "&page=" + pg + "&limit=20", [cat, scope]);
  const cats = (data && data.categories) || [{ key: "top", label: "Top" }];
  const set = (k, v) => { const n = new URLSearchParams(p); n.set(k, v); setP(n, { replace: true }); };
  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6" data-testid="stage-news">
      <h1 className="flex items-center gap-2 font-serif-display text-3xl font-bold"><Newspaper /> News</h1>
      <p className="mt-1 text-muted-foreground">Each story is matched across outlets, with every source linked.{data && data.country ? " Showing " + data.country.label + "." : ""}</p>
      <div className="mt-5 space-y-3">
        <Chips items={cats} value={cat} onPick={(k) => set("category", k)} />
        {data && data.country && data.country.has_local && <Chips items={[{ key: "all", label: "Everything" }, { key: "local", label: data.country.label }, { key: "world", label: "World" }]} value={scope} onPick={(k) => set("scope", k)} />}
      </div>
      {data === undefined && items.length === 0 ? <div className="mt-6 grid gap-4 sm:grid-cols-2"><Skeleton className="h-72 rounded-2xl" /><Skeleton className="h-72 rounded-2xl" /></div>
        : data === null ? <p className="mt-10 text-center text-muted-foreground">Couldn't load the news right now.</p>
        : items.length === 0 ? <p className="mt-10 text-center text-muted-foreground">No stories here yet.</p>
        : <div className="mt-6 grid gap-4 sm:grid-cols-2">{items.map((s) => <StoryCard key={s.id} s={s} />)}</div>}
      {data && data.has_more && <div className="mt-6 text-center"><Button variant="outline" className="rounded-full px-8" onClick={more}>More stories</Button></div>}
    </div>
  );
}

function FixtureCard({ f }) {
  const live = f.state === "in";
  return (
    <div className={cx("rounded-2xl border bg-card p-3", live ? "border-[#E0007A]/50" : "border-border")} data-testid={"fixture-" + f.id}>
      <p className="flex items-center justify-between text-xs text-muted-foreground"><span className="truncate">{f.competition || f.league}</span>{live ? <span className="inline-flex items-center gap-1 font-bold text-[#E0007A]"><Radio size={11} /> {f.status || "Live"}</span> : <span>{f.state === "post" ? f.status || "FT" : f.starts_at ? new Date(f.starts_at).toLocaleString("en-GB", { weekday: "short", hour: "2-digit", minute: "2-digit" }) : ""}</span>}</p>
      {f.kind === "match" && (f.teams || []).length >= 2 ? (
        <div className="mt-2 space-y-1">{f.teams.map((t) => (
          <div key={t.name} className="flex items-center gap-2">
            <span className="grid h-6 w-6 place-items-center overflow-hidden rounded-full text-[10px] font-bold text-white" style={{ background: t.color ? "#" + String(t.color).replace("#", "") : "#888" }}>{t.crest ? <img src={t.crest.url} alt="" className="h-full w-full object-contain" /> : t.abbr}</span>
            <Link to={"/stage/sports/team/" + encodeURIComponent((t.follow && t.follow.key) || t.name) + "?name=" + encodeURIComponent(t.name) + "&league=" + encodeURIComponent(f.league || "")} className={cx("flex-1 truncate text-sm hover:underline", t.winner && "font-bold")}>{t.short || t.name}</Link>
            <span className={cx("w-6 text-right font-mono text-sm", t.winner && "font-bold")}>{t.score != null ? t.score : ""}</span>
          </div>
        ))}</div>
      ) : <p className="mt-2 text-sm font-semibold">{f.name}{f.session ? " · " + f.session.name : ""}</p>}
      {f.venue && <p className="mt-1 truncate text-xs text-muted-foreground">{f.venue}</p>}
    </div>
  );
}

export function StageSports() {
  usePageMeta("Sports", "Scores, fixtures and sport stories on Fable + Stage.");
  const [p, setP] = useSearchParams();
  const league = p.get("league") || "all";
  const { data, items, more } = useStories((pg) => "/news/sports?league=" + league + "&page=" + pg + "&limit=20", [league]);
  const leagues = [{ key: "all", label: "All", count: data && data.all_count }].concat(((data && data.leagues) || []).map((l) => ({ key: l.key, label: l.label, count: l.count })));
  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6" data-testid="stage-sports">
      <h1 className="flex items-center gap-2 font-serif-display text-3xl font-bold"><Trophy /> Sports</h1>
      <Headliners />
      <div className="mt-4"><AgentBrief agent="zach" /></div>
      <TablesStrip />
      <div className="mt-5"><Chips items={leagues} value={league} onPick={(k) => { const n = new URLSearchParams(p); n.set("league", k); setP(n, { replace: true }); }} /></div>
      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div>
          {data === undefined && items.length === 0 ? <Skeleton className="h-72 rounded-2xl" /> : items.length === 0 ? <p className="text-muted-foreground">No sport stories right now.</p> : <div className="grid gap-4 sm:grid-cols-2">{items.map((s) => <StoryCard key={s.id} s={s} />)}</div>}
          {data && data.has_more && <div className="mt-6 text-center"><Button variant="outline" className="rounded-full px-8" onClick={more}>More stories</Button></div>}
        </div>
        <aside className="h-fit space-y-2 lg:sticky lg:top-20">
          <h2 className="font-bold">Fixtures and results</h2>
          <Schedule league={league} />
        </aside>
      </div>
    </div>
  );
}

export function SoundPage() {
  const { id } = useParams();
  const [s, setS] = useState(undefined);
  const [playing, setPlaying] = useState(false);
  const audio = useRef(null);
  usePageMeta(s ? s.title + " · " + s.artist : "Sound", "Videos using this sound on Fable + Stage.");
  useEffect(() => { api("/sounds/" + id).then(setS).catch(() => setS(null)); }, [id]);
  const toggle = () => { const a = audio.current; if (!a) return; if (a.paused) { a.play().then(() => setPlaying(true)).catch(() => null); } else { a.pause(); setPlaying(false); } };
  if (s === undefined) return <div className="mx-auto max-w-3xl px-4 py-8"><Skeleton className="h-40 rounded-2xl" /></div>;
  if (s === null) return <p className="py-20 text-center text-muted-foreground">This sound isn't available.</p>;
  const src = s.preview_url || s.audio_url;
  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6" data-testid="sound-page">
      <div className="flex items-center gap-5">
        <div className="relative h-28 w-28 shrink-0 overflow-hidden rounded-2xl bg-gradient-to-br from-[#E0007A] to-[#8B3CF0]">
          {s.art_url ? <img src={s.art_url} alt="" className="h-full w-full object-cover" /> : <Music2 className="absolute inset-0 m-auto text-white" size={36} />}
          {src && <button type="button" onClick={toggle} aria-label={playing ? "Pause" : "Play"} className="absolute inset-0 grid place-items-center bg-black/25 text-white">{playing ? <Pause size={30} /> : <Play size={30} />}</button>}
        </div>
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-bold">{s.title}</h1>
          <p className="text-muted-foreground">{s.artist}</p>
          <p className="mt-1 text-sm text-muted-foreground">{(s.uses_count || 0).toLocaleString("en-GB")} videos{s.credit ? " · " + s.credit : ""}</p>
        </div>
      </div>
      {src && <audio ref={audio} src={src} preload="none" onEnded={() => setPlaying(false)} />}
      <h2 className="mt-8 font-bold">Videos with this sound</h2>
      {(s.posts || []).length === 0 ? <p className="mt-2 text-sm text-muted-foreground">No public videos yet. Use it in the app.</p> : (
        <div className="mt-3 grid grid-cols-3 gap-1.5 sm:grid-cols-4">{s.posts.map((pp) => <Link key={pp.id} to={"/stage/post/" + pp.id} className="relative aspect-[9/16] overflow-hidden rounded-xl bg-muted">{pp.thumb && <img src={pp.thumb} alt="" loading="lazy" className="h-full w-full object-cover" />}</Link>)}</div>
      )}
      {s.attribution && <p className="mt-6 text-xs text-muted-foreground">{s.attribution}</p>}
    </div>
  );
}

function Countdown({ cd }) {
  if (!cd) return null;
  return <span className="rounded-full bg-white/15 px-2.5 py-1 text-xs font-bold text-white backdrop-blur">{cd.days ? cd.days + "d " + cd.hours + "h" : cd.hours + "h"} to go</span>;
}

export function Headliners() {
  const [items, setItems] = useState(null);
  useEffect(() => { api("/sports/headliners").then((d) => setItems(d.items || [])).catch(() => setItems([])); }, []);
  if (!items || !items.length) return null;
  const [top, ...rest] = items;
  const when = (s) => (s ? new Date(s).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "Date to be confirmed");
  return (
    <section className="mt-6" data-testid="sports-headliners">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#120914] via-[#3a0d2c] to-[#E0007A] p-6 text-white sm:p-8">
        <div className="relative z-10 max-w-[55%]">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-pink-200">The big one</p>
          {top.source === "selinam" && (
            <Link to="/agents/selinam" className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-pink-100/90 hover:text-white" data-testid="found-by-selinam">
              <img src="/agents/selinam-photo.jpg?v=1" alt="" className="h-5 w-5 rounded-full object-cover ring-1 ring-white/40" />Found by Selinam
            </Link>
          )}
<a href={process.env.REACT_APP_BACKEND_URL + "/api/sports/headliners.ics"} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-white underline underline-offset-2" data-testid="headliners-calendar">Add the big events to your calendar</a>
          <h2 className="mt-2 font-serif-display text-3xl font-bold leading-tight sm:text-5xl">{top.title}</h2>
          {top.subtitle && <p className="mt-1 text-sm text-white/80 sm:text-base">{top.subtitle}</p>}
          <p className="mt-3 text-sm text-white/80">{when(top.starts_at)}{top.venue ? " · " + top.venue : ""}{top.watch ? " · " + top.watch : ""}</p>
          <div className="mt-4 flex flex-wrap items-center gap-2"><Countdown cd={top.countdown} />{top.tickets_url && <a href={top.tickets_url} target="_blank" rel="noopener sponsored" className="rounded-full bg-white px-3 py-1 text-xs font-bold text-[#E0007A]">Tickets</a>}</div>
          {top.blurb && <p className="mt-4 hidden text-sm text-white/75 sm:block">{top.blurb}</p>}
        </div>
        <div className="absolute inset-y-0 right-0 flex w-[50%] items-end justify-end gap-0">
          {(top.participants || []).slice(0, 2).map((p, i) => p.photo ? <img key={p.name} src={p.photo} alt={p.name} loading="lazy" className={cx("h-[92%] w-1/2 object-contain object-bottom drop-shadow-2xl", i === 1 && "-scale-x-100")} /> : null)}
        </div>
      </div>
      {rest.length > 0 && (
        <div className="mt-3 flex gap-3 overflow-x-auto pb-1">
          {rest.map((h) => (
            <div key={h.id || h.title} className="min-w-[240px] rounded-2xl border border-border bg-card p-3">
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#E0007A]">{h.sport}</p>
              <p className="mt-0.5 truncate font-semibold">{h.title}</p>
              <p className="truncate text-xs text-muted-foreground">{when(h.starts_at)}{h.venue ? " · " + h.venue : ""}</p>
              <div className="mt-2 flex -space-x-2">{(h.participants || []).map((p) => p.photo ? <img key={p.name} src={p.photo} alt={p.name} className="h-8 w-8 rounded-full border-2 border-card bg-muted object-cover object-top" /> : null)}</div>
            </div>
          ))}
        </div>
      )}
      <p className="mt-1 text-right text-[10px] text-muted-foreground">Photos: TheSportsDB · Wikimedia Commons</p>
    </section>
  );
}

const DAY_TABS = [["live", "Live"], ["today", "Today"], ["tomorrow", "Tomorrow"], ["week", "This week"], ["results", "Results"]];
export function Schedule({ league }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("today");
  useEffect(() => {
    let alive = true;
    const load = () => api("/sports/schedule?league=" + league).then((x) => { if (!alive) return; setD(x); if (x.has_live) setTab((t) => (t === "today" ? "live" : t)); }).catch(() => alive && setD({ buckets: {} }));
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [league]);
  const list = (d && d.buckets && d.buckets[tab]) || [];
  return (
    <div>
      <div className="flex gap-1 overflow-x-auto pb-2">{DAY_TABS.map(([k, l]) => <button key={k} type="button" onClick={() => setTab(k)} aria-pressed={tab === k} className={cx("whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold", tab === k ? "bg-foreground text-background" : "bg-muted text-muted-foreground")}>{l}{d && d.buckets && d.buckets[k] && d.buckets[k].length ? " " + d.buckets[k].length : ""}</button>)}</div>
      {d === null ? <Skeleton className="h-48 rounded-2xl" /> : list.length === 0 ? <p className="py-4 text-sm text-muted-foreground">Nothing {tab === "live" ? "live right now" : tab === "results" ? "finished yet" : "scheduled"}.</p> : <div className="space-y-2">{list.slice(0, 30).map((f) => <FixtureCard key={f.id} f={f} />)}</div>}
    </div>
  );
}

export function TeamPage() {
  const { key } = useParams();
  const [p] = useSearchParams();
  const [d, setD] = useState(undefined);
  usePageMeta((d && d.team && d.team.name) || "Team", "Fixtures, results and stories.");
  useEffect(() => { api("/sports/team/" + encodeURIComponent(key) + "?" + new URLSearchParams({ ...(p.get("name") ? { name: p.get("name") } : {}), ...(p.get("league") ? { league: p.get("league") } : {}) }).toString()).then(setD).catch(() => setD(null)); }, [key, p]);
  if (d === undefined) return <div className="mx-auto max-w-5xl px-4 py-8"><Skeleton className="h-40 rounded-2xl" /></div>;
  if (d === null) return <p className="py-20 text-center text-muted-foreground">We couldn't find that team.</p>;
  const t = d.team || {};
  const crest = t.crest && (t.crest.url || t.crest);
  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6" data-testid="team-page">
      <Link to="/stage/sports" className="text-sm text-muted-foreground hover:text-foreground">← Sports</Link>
      <div className="mt-3 flex items-center gap-4">
        <span className="grid h-20 w-20 place-items-center overflow-hidden rounded-2xl text-2xl font-extrabold text-white" style={{ background: t.color ? "#" + String(t.color).replace("#", "") : "#E0007A" }}>{crest ? <img src={crest} alt="" className="h-full w-full object-contain p-2" /> : (t.abbr || (t.name || "?").slice(0, 3))}</span>
        <div><h1 className="font-serif-display text-3xl font-bold">{t.name}</h1><p className="text-muted-foreground">{[t.league_label || t.league, t.position ? "Position " + t.position : null, t.record].filter(Boolean).join(" · ")}</p></div>
      </div>
      <a href={process.env.REACT_APP_BACKEND_URL + "/api/sports/team/" + encodeURIComponent(t.key || key) + "/calendar.ics?name=" + encodeURIComponent(t.name || "") + (t.league ? "&league=" + encodeURIComponent(t.league) : "")}
  className="mt-4 inline-flex h-10 items-center gap-2 rounded-full border px-4 text-sm font-semibold hover:bg-muted" data-testid="team-calendar">
  <span aria-hidden="true">📅</span> Add fixtures to your calendar
</a>
<TeamDepth name={t.name} />
      <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div>
          <h2 className="font-bold">Latest stories</h2>
          {(d.stories || []).length === 0 ? <p className="mt-2 text-sm text-muted-foreground">No stories right now.</p> : <div className="mt-3 grid gap-4 sm:grid-cols-2">{d.stories.map((s) => <StoryCard key={s.id} s={s} />)}</div>}
        </div>
        <aside className="space-y-5">
          <div><h2 className="mb-2 font-bold">Next up</h2>{(d.next || []).length === 0 ? <p className="text-sm text-muted-foreground">No fixtures announced.</p> : <div className="space-y-2">{d.next.map((f) => <FixtureCard key={f.id} f={f} />)}</div>}</div>
          <div><h2 className="mb-2 font-bold">Form</h2><div className="flex gap-1.5">{(d.last || []).map((f) => <span key={f.id} title={f.name} className={cx("grid h-8 w-8 place-items-center rounded-lg text-xs font-bold text-white", f.result === "W" ? "bg-emerald-500" : f.result === "L" ? "bg-rose-500" : "bg-zinc-400")}>{f.result || "–"}</span>)}</div>
            <div className="mt-2 space-y-2">{(d.last || []).slice(0, 5).map((f) => <FixtureCard key={f.id} f={f} />)}</div></div>
        </aside>
      </div>
    </div>
  );
}

function useDb(path) {
  const [d, setD] = useState(undefined);
  useEffect(() => { if (!path) return; let alive = true; setD(undefined); api(path).then((x) => alive && setD(x)).catch(() => alive && setD(null)); return () => { alive = false; }; }, [path]);
  return d;
}

export function TeamDepth({ name }) {
  const d = useDb(name ? "/sports/db/team?name=" + encodeURIComponent(name) : null);
  const [more, setMore] = useState(false);
  if (d === undefined) return <Skeleton className="mt-6 h-40 rounded-2xl" />;
  if (!d) return null;
  return (
    <section className="mt-8 space-y-6" data-testid="team-depth">
      <div className="grid gap-3 sm:grid-cols-4">
        {[["Founded", d.founded], ["Stadium", d.stadium], ["Capacity", d.capacity ? Number(d.capacity).toLocaleString("en-GB") : null], ["League", d.league]].filter(([, v]) => v).map(([k, v]) => (
          <div key={k} className="rounded-2xl border border-border bg-card p-3"><p className="text-xs text-muted-foreground">{k}</p><p className="mt-0.5 font-semibold">{k === "League" && d.league_id ? <Link to={"/stage/sports/league/" + d.league_id} className="hover:underline">{v}</Link> : v}</p></div>
        ))}
      </div>
      {d.history && <div><h2 className="font-bold">History</h2><p className={cx("mt-2 whitespace-pre-line text-sm leading-relaxed text-muted-foreground", !more && "line-clamp-5")}>{d.history}</p><button type="button" onClick={() => setMore(!more)} className="mt-1 text-sm font-semibold text-[#E0007A]">{more ? "Less" : "Read more"}</button></div>}
      {(d.squad || []).length > 0 && (
        <div>
          <h2 className="font-bold">Squad</h2>
          <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-5 lg:grid-cols-6">
            {d.squad.map((p) => (
              <Link key={p.id} to={"/stage/sports/player/" + encodeURIComponent(p.name)} className="group overflow-hidden rounded-2xl border border-border bg-card text-center">
                <div className="aspect-square bg-gradient-to-b from-muted to-card">{p.cutout || p.photo ? <img src={p.cutout || p.photo} alt={p.name} loading="lazy" className="h-full w-full object-contain object-bottom transition-transform group-hover:scale-105" /> : null}</div>
                <p className="truncate px-2 pt-2 text-xs font-semibold">{p.name}</p>
                <p className="flex items-center justify-center gap-1 px-2 pb-2 text-[11px] text-muted-foreground"><Flag country={p.nationality} size={10} /> {p.position}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
      {(d.last || []).length > 0 && (
        <div>
          <h2 className="font-bold">Recent results</h2>
          <ul className="mt-2 divide-y divide-border rounded-2xl border border-border bg-card">{d.last.map((e) => (
            <li key={e.id} className="flex items-center gap-2 px-3 py-2 text-sm">
              <span className="w-20 shrink-0 text-xs text-muted-foreground">{e.starts_at ? new Date(e.starts_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : ""}</span>
              <span className="flex flex-1 items-center justify-end gap-1.5 truncate">{e.home}{e.home_badge && <img src={e.home_badge + "/tiny"} alt="" className="h-5 w-5" />}</span>
              <span className="w-12 text-center font-mono font-bold">{e.home_score}–{e.away_score}</span>
              <span className="flex flex-1 items-center gap-1.5 truncate">{e.away_badge && <img src={e.away_badge + "/tiny"} alt="" className="h-5 w-5" />}{e.away}</span>
            </li>
          ))}</ul>
        </div>
      )}
      <p className="text-[10px] text-muted-foreground">Team data and photos: TheSportsDB</p>
    </section>
  );
}

export function PlayerPage() {
  const { name } = useParams();
  const d = useDb("/sports/db/player?name=" + encodeURIComponent(name));
  usePageMeta((d && d.name) || "Player", "Bio, career and honours.");
  const [more, setMore] = useState(false);
  if (d === undefined) return <div className="mx-auto max-w-5xl px-4 py-8"><Skeleton className="h-72 rounded-3xl" /></div>;
  if (!d) return <p className="py-20 text-center text-muted-foreground">We couldn't find that player.</p>;
  const age = d.born ? Math.floor((Date.now() - new Date(d.born).getTime()) / 31557600000) : null;
  const byHonour = {};
  (d.honours || []).forEach((h) => { (byHonour[h.honour] = byHonour[h.honour] || []).push(h); });
  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6" data-testid="player-page">
      <Link to="/stage/sports" className="text-sm text-muted-foreground hover:text-foreground">← Sports</Link>
      <div className="relative mt-3 overflow-hidden rounded-3xl bg-gradient-to-br from-[#120914] via-[#3a0d2c] to-[#E0007A] p-6 text-white sm:p-8">
        <div className="relative z-10 max-w-[60%]">
          <p className="flex items-center gap-2 text-sm text-white/80"><Flag country={d.nationality} size={14} /> {d.nationality}{d.position ? " · " + d.position : ""}{d.number ? " · #" + d.number : ""}</p>
          <h1 className="mt-2 font-serif-display text-4xl font-bold leading-tight sm:text-5xl">{d.name}</h1>
          <p className="mt-2 text-white/80">{d.team ? <Link to={"/stage/sports/team/" + encodeURIComponent(d.team) + "?name=" + encodeURIComponent(d.team)} className="underline-offset-2 hover:underline">{d.team}</Link> : null}{age ? " · " + age + " years old" : ""}</p>
          <div className="mt-4 flex flex-wrap gap-2 text-xs">{[["Height", d.height], ["Weight", d.weight], ["Foot", d.foot], ["Born", d.birthplace]].filter(([, v]) => v).map(([k, v]) => <span key={k} className="rounded-full bg-white/15 px-3 py-1">{k}: {v}</span>)}</div>
        </div>
        {(d.cutout || d.photo) && <img src={d.cutout || d.photo} alt={d.name} className="absolute bottom-0 right-4 h-[95%] max-w-[45%] object-contain object-bottom drop-shadow-2xl" />}
      </div>
      <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div>
          {d.bio && <><h2 className="font-bold">Story</h2><p className={cx("mt-2 whitespace-pre-line text-sm leading-relaxed text-muted-foreground", !more && "line-clamp-6")}>{d.bio}</p><button type="button" onClick={() => setMore(!more)} className="mt-1 text-sm font-semibold text-[#E0007A]">{more ? "Less" : "Read more"}</button></>}
          {Object.keys(byHonour).length > 0 && (
            <div className="mt-8"><h2 className="font-bold">Honours</h2>
              <ul className="mt-3 grid gap-2 sm:grid-cols-2">{Object.entries(byHonour).map(([h, list]) => (
                <li key={h} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3">
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-amber-500/10 text-lg font-extrabold text-amber-600">{list.length}</span>
                  <span className="min-w-0"><span className="block truncate font-semibold">{h}</span><span className="text-xs text-muted-foreground">{list.map((x) => x.season).filter(Boolean).slice(0, 4).join(", ")}{list.length > 4 ? "…" : ""}</span></span>
                </li>
              ))}</ul>
            </div>
          )}
        </div>
        <aside>
          <h2 className="font-bold">Career</h2>
          {(d.career || []).length === 0 ? <p className="mt-2 text-sm text-muted-foreground">No transfer history listed.</p> : (
            <ol className="mt-3 space-y-2 border-l-2 border-[#E0007A]/30 pl-4">{d.career.map((c, i) => (
              <li key={i} className="relative"><span className="absolute -left-[21px] top-2 h-2.5 w-2.5 rounded-full bg-[#E0007A]" />
                <p className="flex items-center gap-2 text-sm font-semibold">{c.badge && <img src={c.badge + "/tiny"} alt="" className="h-5 w-5" />}{c.team}</p>
                <p className="text-xs text-muted-foreground">{[c.joined, c.departed].filter(Boolean).join(" → ")}{c.move ? " · " + c.move : ""}</p></li>
            ))}</ol>
          )}
        </aside>
      </div>
      <p className="mt-8 text-[10px] text-muted-foreground">Player data and photos: TheSportsDB</p>
    </div>
  );
}

export function LeaguePage() {
  const { id } = useParams();
  const d = useDb("/sports/db/league?id=" + encodeURIComponent(id));
  usePageMeta((d && d.name) || "League", "Table, history and teams.");
  const [more, setMore] = useState(false);
  if (d === undefined) return <div className="mx-auto max-w-5xl px-4 py-8"><Skeleton className="h-72 rounded-3xl" /></div>;
  if (!d) return <p className="py-20 text-center text-muted-foreground">League not found.</p>;
  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6" data-testid="league-page">
      <Link to="/stage/sports" className="text-sm text-muted-foreground hover:text-foreground">← Sports</Link>
      <div className="mt-3 flex items-center gap-4">{d.badge && <img src={d.badge} alt="" className="h-20 w-20 object-contain" />}<div><h1 className="font-serif-display text-3xl font-bold">{d.name}</h1><p className="flex items-center gap-2 text-muted-foreground"><Flag country={d.country} size={12} /> {[d.country, d.founded ? "since " + d.founded : null, d.season].filter(Boolean).join(" · ")}</p></div>{d.trophy && <img src={d.trophy} alt="Trophy" className="ml-auto hidden h-24 object-contain sm:block" />}</div>
      {(d.table || []).length > 0 && (
        <div className="mt-6 overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-xs text-muted-foreground">{["#", "Team", "P", "W", "D", "L", "GD", "Pts", "Form"].map((h) => <th key={h} className={cx("px-2 py-2 font-medium", h === "Team" ? "text-left" : "text-center")}>{h}</th>)}</tr></thead>
            <tbody>{d.table.map((r) => (
              <tr key={r.team} className="border-b border-border/60 last:border-0">
                <td className="px-2 py-2 text-center font-semibold">{r.rank}</td>
                <td className="px-2 py-2"><Link to={"/stage/sports/team/" + encodeURIComponent(r.team) + "?name=" + encodeURIComponent(r.team)} className="flex items-center gap-2 font-medium hover:underline">{r.badge && <img src={r.badge + "/tiny"} alt="" className="h-5 w-5" />}{r.team}</Link></td>
                {[r.played, r.won, r.drawn, r.lost, r.gd].map((v, i) => <td key={i} className="px-2 py-2 text-center tabular-nums">{v}</td>)}
                <td className="px-2 py-2 text-center font-bold tabular-nums">{r.points}</td>
                <td className="px-2 py-2"><span className="flex justify-center gap-0.5">{String(r.form || "").slice(-5).split("").map((c, i) => <span key={i} className={cx("grid h-4 w-4 place-items-center rounded-sm text-[9px] font-bold text-white", c === "W" ? "bg-emerald-500" : c === "L" ? "bg-rose-500" : "bg-zinc-400")}>{c}</span>)}</span></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {d.history && <div className="mt-8"><h2 className="font-bold">History</h2><p className={cx("mt-2 whitespace-pre-line text-sm leading-relaxed text-muted-foreground", !more && "line-clamp-6")}>{d.history}</p><button type="button" onClick={() => setMore(!more)} className="mt-1 text-sm font-semibold text-[#E0007A]">{more ? "Less" : "Read more"}</button></div>}
      <p className="mt-8 text-[10px] text-muted-foreground">League data: TheSportsDB</p>
    </div>
  );
}

const TABLES = [[4328, "Premier League", "england"], [4329, "Championship", "england"], [4335, "La Liga", "spain"], [4332, "Serie A", "italy"], [4331, "Bundesliga", "germany"], [4334, "Ligue 1", "france"], [4330, "Scottish Prem", "scotland"]];
export function TablesStrip() {
  return (
    <div className="mt-4 flex gap-2 overflow-x-auto pb-1" data-testid="sports-tables">
      <span className="self-center whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tables</span>
      {TABLES.map(([id, name, c]) => <Link key={id} to={"/stage/sports/league/" + id} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-card px-3 py-1.5 text-sm hover:bg-muted"><Flag country={c} size={11} /> {name}</Link>)}
    </div>
  );
}
